import React from 'react';
import moment from 'moment';
import {
  JobsitePulseContextType,
  JobsitePulseFilters,
} from 'containers/jobsite/tabs/jobsitePulse/types';
import { JobsiteTabProps } from 'containers/jobsite/types';
import { FilterItems } from '@odin-labs/components';
import { useJobsitePulseData } from 'containers/jobsite/tabs/jobsitePulse/data';
import { usePageQueryParams } from 'utils/usePageQueryParams';
import { getJobsiteDateTimeNow } from 'utils/dates';

export const JobsitePulseContext =
  React.createContext<JobsitePulseContextType | null>(null);

export function useJobsitePulse(): JobsitePulseContextType {
  const context = React.useContext(JobsitePulseContext);
  if (!context) {
    throw new Error(
      'useJobsitePulse must be used within a JobsitePulseProvider',
    );
  }
  return context;
}

type JobsitePulseProviderProps = React.PropsWithChildren<JobsiteTabProps>;

export function JobsitePulseProvider(
  props: JobsitePulseProviderProps,
): React.ReactElement {
  const { children, jobsiteId, onTabApiChange, loading } = props;

  const { timeZone } = props;
  const { defaultDate, defaultTime } = React.useMemo(() => {
    const { date, time } = timeZone ? getJobsiteDateTimeNow({ timeZone }) : {};
    return {
      defaultDate: moment(date).startOf('day'),
      defaultTime: time,
    };
  }, [timeZone]);

  const {
    date,
    time,
    updateUrl,
    loading: isUrlLoading,
  } = usePageQueryParams({
    defaultValues: {
      date: defaultDate,
      time: defaultTime,
    },
  });

  const applyFilter = (changedFilters: Partial<JobsitePulseFilters>): void => {
    updateUrl(changedFilters, true);
  };

  const pulseState = useJobsitePulseData({
    jobsiteId,
    date,
    time: time || defaultTime,
    skip: isUrlLoading || loading,
  });
  const { refetch } = pulseState;

  React.useEffect(
    () => onTabApiChange({ refetchData: refetch }),
    [onTabApiChange, refetch],
  );

  const filterItems = React.useMemo(
    (): FilterItems<JobsitePulseFilters> => ({
      date: {
        header: '',
        type: 'datePicker',
        defaultValue: date,
        componentProps: {
          numberOfMonths: 1,
        },
      },
    }),
    [date?.toISOString()],
  );

  const value = React.useMemo(
    (): JobsitePulseContextType => ({
      ...pulseState,
      filterItems,
      jobsiteId,
      applyFilter,
    }),
    [pulseState, filterItems, jobsiteId, applyFilter],
  );

  return (
    <JobsitePulseContext.Provider value={value}>
      {children}
    </JobsitePulseContext.Provider>
  );
}
